import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";

import type { PendingUserInputDraftAnswer } from "./pendingUserInput";
import { normalizePersistedFileAttachment } from "./composerDraftAttachments";

export const PENDING_USER_INPUT_DRAFT_STORAGE_PREFIX = "forge:pending-user-input-draft:";

export type PendingUserInputDrafts = Record<string, PendingUserInputDraftAnswer>;

function storageKey(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly requestId: string;
}): string {
  return `${PENDING_USER_INPUT_DRAFT_STORAGE_PREFIX}${input.environmentId}:${input.threadId}:${input.requestId}`;
}

/** Returns null when persisted question data cannot prove every file reference shape. */
export function readPersistedPendingUserInputFileAttachmentIds(
  raw: string | null,
): Set<string> | null {
  if (raw === null) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const ids = new Set<string>();
    for (const value of Object.values(parsed as Record<string, unknown>)) {
      const attachmentIds = readPendingQuestionFileAttachmentIds(value);
      if (attachmentIds === null) return null;
      for (const id of attachmentIds) ids.add(id);
    }
    return ids;
  } catch {
    return null;
  }
}

function readPendingQuestionFileAttachmentIds(value: unknown): Set<string> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const attachments = (value as Record<string, unknown>).attachments;
  if (attachments === undefined) return new Set();
  if (!Array.isArray(attachments)) return null;
  const ids = new Set<string>();
  for (const attachment of attachments) {
    const ref = normalizePersistedFileAttachment(attachment);
    if (!ref) return null;
    ids.add(ref.id);
  }
  return ids;
}

function getStorage(): Storage | null {
  return typeof localStorage === "undefined" ? null : localStorage;
}

function normalizeSelectedOptionLabels(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const labels = value.filter((label): label is string => typeof label === "string");
  return labels.length > 0 ? labels : undefined;
}

function normalizeAttachments(
  value: unknown,
): PendingUserInputDraftAnswer["attachments"] | undefined {
  if (!Array.isArray(value)) return undefined;
  const attachments = value.flatMap((attachment) => {
    const ref = normalizePersistedFileAttachment(attachment);
    return ref ? [{ ...ref, type: "file" as const, file: null }] : [];
  });
  return attachments.length > 0 ? attachments : undefined;
}

function normalizeDraft(value: unknown): PendingUserInputDraftAnswer | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  return buildDraftAnswer({
    selectedOptionLabels: normalizeSelectedOptionLabels(candidate.selectedOptionLabels),
    customAnswer: typeof candidate.customAnswer === "string" ? candidate.customAnswer : undefined,
    attachments: normalizeAttachments(candidate.attachments),
  });
}

function buildDraftAnswer(input: {
  readonly selectedOptionLabels: string[] | undefined;
  readonly customAnswer: string | undefined;
  readonly attachments: PendingUserInputDraftAnswer["attachments"] | undefined;
}): PendingUserInputDraftAnswer | null {
  const value: PendingUserInputDraftAnswer = {};
  if (input.selectedOptionLabels) value.selectedOptionLabels = input.selectedOptionLabels;
  if (input.customAnswer !== undefined) value.customAnswer = input.customAnswer;
  if (input.attachments) value.attachments = input.attachments;
  return Object.keys(value).length > 0 ? value : null;
}

function normalizeDrafts(value: unknown): PendingUserInputDrafts {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).flatMap(([questionId, value]) => {
      const draft = normalizeDraft(value);
      return draft ? [[questionId, draft]] : [];
    }),
  );
}

export function readPendingUserInputDrafts(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly requestId: string;
}): PendingUserInputDrafts {
  try {
    const serialized = getStorage()?.getItem(storageKey(input));
    return serialized ? normalizeDrafts(JSON.parse(serialized)) : {};
  } catch {
    return {};
  }
}

export function writePendingUserInputDrafts(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly requestId: string;
  readonly drafts: PendingUserInputDrafts;
}): boolean {
  try {
    const serialized = JSON.stringify(
      Object.fromEntries(
        Object.entries(input.drafts).map(([questionId, answer]) => [
          questionId,
          {
            ...(answer.selectedOptionLabels
              ? { selectedOptionLabels: answer.selectedOptionLabels }
              : {}),
            ...(answer.customAnswer !== undefined ? { customAnswer: answer.customAnswer } : {}),
            ...(answer.attachments
              ? {
                  attachments: answer.attachments.map(
                    ({ file: _file, type: _type, ...ref }) => ref,
                  ),
                }
              : {}),
          },
        ]),
      ),
    );
    getStorage()?.setItem(storageKey(input), serialized);
    return true;
  } catch {
    return false;
  }
}

export function clearPendingUserInputDrafts(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly requestId: string;
}): void {
  try {
    getStorage()?.removeItem(storageKey(input));
  } catch {
    // The provider response remains valid even when browser storage is unavailable.
  }
}
